import db from "../db/database";
import { markForDelete, markForSync } from "./syncService";
import { buscarValorEnUso, buscarValorEnUsoAlEditar } from "./unicidad";
import { getActiveLocationCode } from "./cajaEstadoService";

// Servicio para manejar las operaciones relacionadas con los productos

// Agregar un nuevo producto a la base de datos
export const addProduct = async (product) => {
  try {
    const descripcionEnUso = await buscarValorEnUso(db.products, "description", product.description);

    if (descripcionEnUso) {
      throw new Error(
        `Ya existe un producto con esa descripción: ${descripcionEnUso.description} (No. ${descripcionEnUso.No || "pendiente"})`
      );
    }

    // Verificar si el código de barras ya existe y no está vacío
    if (product.barcode) {
      const codigoEnUso = await buscarValorEnUso(db.products, "barcode", product.barcode);

      if (codigoEnUso) {
        throw new Error(
          `Ya existe un producto con ese código de barras: ${codigoEnUso.description}`
        );
      }
    }

    // Agregar el producto a la base de datos
    const id = await db.products.add({
      description: product.description,
      Unit_Price: product.Unit_Price || 0, // Agregar precio unitario
      barcode: product.barcode || null, // Agregar código de barras
      PorIva: product.PorIva || 0, // Agregar porcentaje de IVA
      createdAt: new Date(),
      pendingSync: 1, // Marcar como pendiente de sincronización (usando 1 en lugar de true)
      lastModified: new Date(),
      No: product.No || null, // Guardar el No de Business Central si existe
      deleted: false,
    });

    return { id, ...product };
  } catch (error) {
    console.error("Error al agregar producto:", error);
    throw error;
  }
};

// Obtener todos los productos
/**
 * Todos los productos que le corresponden a la tienda activa.
 *
 * `locationCode` se puede pasar ya resuelto. No es una micro-optimización: para
 * averiguarlo hay que leer la apertura activa, escribir su respaldo y consultar
 * la tienda --tres viajes a la base antes de tocar un solo producto-- y la
 * pantalla de productos lo necesitaba dos veces seguidas, una para la vista
 * previa y otra para el catálogo completo.
 */
export const getAllProducts = async (locationCodeYaResuelto) => {
  try {
    // Si la tienda activa ya sincronizó productos con stock por ubicación,
    // devolver sólo esos (evita mezclar precio/stock de otra tienda que haya
    // quedado guardada localmente). Países/tiendas que no usan stock por
    // ubicación (locationCode nunca seteado) siguen viendo el catálogo
    // completo como hasta ahora.
    const locationCode = locationCodeYaResuelto ?? (await getActiveLocationCode());

    if (locationCode) {
      const scoped = await db.products
        .where("locationCode")
        .equals(locationCode)
        .filter((product) => !product.deleted)
        .toArray();

      if (scoped.length > 0) {
        return scoped;
      }
    }

    // Obtener solo productos no marcados como eliminados
    const products = await db.products.filter((product) => !product.deleted).toArray();
    return products;
  } catch (error) {
    console.error("Error al obtener productos:", error);
    throw error;
  }
};

// Vista previa rápida (los primeros `limit`) para pintar la pantalla al
// instante mientras se carga el catálogo completo por atrás. Dexie corta el
// recorrido de la tabla apenas junta `limit` coincidencias, así que es rápida
// sin importar el tamaño total del catálogo.
/**
 * Los primeros `limit` productos, para pintar la pantalla al instante.
 *
 * Mira **la misma tabla que `getAllProducts`**, y eso no es un detalle. Antes
 * la vista previa recorría el catálogo entero sin filtrar por tienda mientras
 * la lista completa sí filtraba: si la base local guardaba productos de más de
 * una tienda, la previa pintaba cincuenta que podían no ser de la tienda
 * activa, y un instante después la lista los reemplazaba por otros. No era sólo
 * lento -- era un parpadeo con datos equivocados.
 *
 * La regla del respaldo es la misma que allá: si la tienda activa no tiene
 * ninguno, se muestra el catálogo completo. Si no, los países que no usan stock
 * por tienda verían la pantalla vacía un segundo.
 */
export const getProductsPreview = async (limit = 50, locationCode) => {
  try {
    if (locationCode) {
      const scoped = await db.products
        .where("locationCode")
        .equals(locationCode)
        .filter((product) => !product.deleted)
        .limit(limit)
        .toArray();

      if (scoped.length > 0) {
        return scoped;
      }
    }

    const products = await db.products
      .filter((product) => !product.deleted)
      .limit(limit)
      .toArray();
    return products;
  } catch (error) {
    console.error("Error al obtener vista previa de productos:", error);
    throw error;
  }
};

// Obtener un producto por ID local
export const getProductById = async (id) => {
  try {
    const product = await db.products.get(id);
    if (!product || product.deleted) {
      throw new Error("Producto no encontrado");
    }
    return product;
  } catch (error) {
    console.error(`Error al obtener producto con ID ${id}:`, error);
    throw error;
  }
};

// Obtener un producto por No de Business Central
export const getProductByNo = async (no) => {
  try {
    const product = await db.products
      .where("No")
      .equals(no)
      .and((product) => !product.deleted)
      .first();

    if (!product) {
      throw new Error("Producto no encontrado");
    }
    return product;
  } catch (error) {
    console.error(`Error al obtener producto con No ${no}:`, error);
    throw error;
  }
};

// Los `No` de los productos que son bolsa (`Plastic_Bag`, hoy sólo Perú). Una
// bolsa no se devuelve: ver `esLineaDeBolsa` en utils/impuestoDeBolsas.js.
//
// `Plastic_Bag` no está indexado, así que se recorre la tabla; se pide una vez
// por operación, no por línea.
export const numerosDeLasBolsas = async () => {
  const bolsas = await db.products.filter((p) => p.Plastic_Bag === true && !p.deleted).toArray();
  return new Set(bolsas.map((p) => p.No));
};

// Obtener un producto por código de barras
export const getProductByBarcode = async (barcode) => {
  try {
    const product = await db.products
      .where("barcode")
      .equals(barcode)
      .and((product) => !product.deleted)
      .first();

    if (!product) {
      return null; // Devolver null en lugar de lanzar error para facilitar la validación
    }
    return product;
  } catch (error) {
    console.error(`Error al obtener producto con código de barras ${barcode}:`, error);
    throw error;
  }
};

// Ajusta el stock local (optimista) de un producto en una tienda puntual, sin
// esperar el próximo refetch a Business Central. Se usa al agregar/editar/
// eliminar líneas de factura -- ver InvoiceLines.js.
// quantityDelta = cantidadNueva - cantidadVieja de la línea (positivo si se
// agregó/aumentó cantidad, negativo si se quitó/redujo). El stock local se
// resta esa cantidad, permitiendo quedar negativo (no bloquea la venta).
export const adjustLocalStock = async (no, locationCode, quantityDelta) => {
  if (!no || !locationCode || !quantityDelta) return;

  try {
    const product = await db.products.where("[No+locationCode]").equals([no, locationCode]).first();

    if (!product) return;

    await db.products.update(product.id, {
      Stock_Quantity: (product.Stock_Quantity || 0) - quantityDelta,
    });
  } catch (error) {
    console.error(`Error al ajustar stock local de ${no}:`, error);
  }
};

// Actualizar un producto existente
export const updateProduct = async (id, productData) => {
  try {
    const product = await db.products.get(id);
    if (!product || product.deleted) {
      throw new Error("Producto no encontrado");
    }

    const descripcionEnUso = await buscarValorEnUsoAlEditar(
      db.products,
      "description",
      productData,
      product
    );

    if (descripcionEnUso) {
      throw new Error(
        `Ya existe un producto con esa descripción: ${descripcionEnUso.description} (No. ${descripcionEnUso.No || "pendiente"})`
      );
    }

    const codigoEnUso = await buscarValorEnUsoAlEditar(db.products, "barcode", productData, product);

    if (codigoEnUso) {
      throw new Error(
        `Ya existe un producto con ese código de barras: ${codigoEnUso.description}`
      );
    }

    // Actualizar el producto
    await db.products.update(id, {
      ...productData,
      lastModified: new Date(),
    });

    // Marcar como pendiente de sincronización
    await markForSync("products", id);

    return await getProductById(id);
  } catch (error) {
    console.error(`Error al actualizar producto con ID ${id}:`, error);
    throw error;
  }
};

// Eliminar un producto
export const deleteProduct = async (id) => {
  try {
    const product = await db.products.get(id);
    if (!product) {
      throw new Error("Producto no encontrado");
    }

    // Marcar como eliminado y pendiente de sincronización
    await markForDelete("products", id);

    return true;
  } catch (error) {
    console.error(`Error al eliminar producto con ID ${id}:`, error);
    throw error;
  }
};
